import { useField } from 'formik';
import _ from 'lodash';
import {
	DragDropContext,
	Draggable,
	DraggableProvided,
	DraggableStateSnapshot,
	Droppable,
} from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import { AddIcon, Button, Separator } from '@treams/treams-ui';

import AddButton from './AddButton';
import ItemCard from './ItemCard';
import SectionCard from './SectionCard';

type Section = graphql.EvaluationFormSection & { id: string };
type Item = graphql.EvaluationSectionItem & { id: string };

export default function Preview() {
	const [field, , { setValue }] = useField<Section[]>('sections');
	const { t } = useTranslation();

	const sections = field.value;

	const finalScoreItem = sections
		.flatMap((section) => section.items)
		.find((item) => item.type === graphql.EvaluationSectionItemType.FinalScore);
	const isWeighted =
		finalScoreItem?.finalScore?.calculationMethod ===
		graphql.EvaluationFinalScoreCalculationMethod.Average;
	const weightTotal = sections.reduce((total, section) => total + (section.weight ?? 0), 0);
	const weightError =
		weightTotal > 100
			? t('common.settings.evaluationForms.section.weight.totalExceeded')
			: undefined;

	function renderItem(item: Item, index: number, draggable: boolean) {
		const onUpdate = (id: string, update?: Item) =>
			setValue(
				sections.map((section) => ({
					...section,
					items: section.items.map((item) => (item.id === id ? (update ?? item) : item)),
				})),
			);

		const onDelete = (id: string) =>
			setValue(
				sections.map((section) => ({
					...section,
					items: section.items.filter((item) => item.id !== id),
				})),
			);

		if (draggable) {
			return (
				<Draggable key={item.id} draggableId={item.id} index={index}>
					{(provided, snapshot) => (
						<ItemCard
							key={item.id}
							item={item}
							dragging={snapshot.isDragging}
							provided={provided}
							onUpdate={onUpdate}
							onDelete={onDelete}
						/>
					)}
				</Draggable>
			);
		} else {
			return (
				<ItemCard
					key={item.id}
					item={item}
					dragging={false}
					onUpdate={onUpdate}
					onDelete={onDelete}
				/>
			);
		}
	}

	function renderSection(
		section: { id: string; items: Item[]; weight?: number | null },
		index: number,
		provided?: DraggableProvided,
		snapshot?: DraggableStateSnapshot,
	) {
		const contents = section.items.map((item, index) => renderItem(item, index, !!provided));

		const onDelete = (id: string) => {
			setValue(sections.filter((section) => section.id !== id));
		};

		const weight = section.weight ?? undefined;

		const onWeightChange = (weight?: number) => {
			setValue(sections.map((match) => (match.id === section.id ? { ...match, weight } : match)));
		};

		return (
			<SectionCard
				id={section.id}
				onDelete={onDelete}
				section={index}
				dragHandleProps={provided?.dragHandleProps}
				dragging={snapshot?.isDragging ?? false}
				weight={
					isWeighted
						? {
								value: weight,
								error: weight !== undefined ? weightError : undefined,
								onChange: onWeightChange,
							}
						: undefined
				}
			>
				<Droppable droppableId={section.id} type="ITEM">
					{(provided) => (
						<Box {...provided.droppableProps} ref={provided.innerRef}>
							{contents}
							{provided.placeholder}
							<AddButton
								existingTypes={sections
									.map((section) => section.items)
									.flat()
									.map((item) => item.type)}
								onCreate={(item) => {
									const result = _.cloneDeep(sections);

									result
										.find((match) => match.id === section.id)
										?.items.push({
											id: `__${Math.random()}`,
											...item,
										} as unknown as Item);
									setValue(result);
								}}
							/>
						</Box>
					)}
				</Droppable>
			</SectionCard>
		);
	}

	return (
		<DragDropContext
			onDragEnd={(dnd) => {
				if (!dnd.destination) {
					return;
				}

				const result = _.cloneDeep(sections);

				if (dnd.type === 'ITEM') {
					const source = result.find((section) => section.id === dnd.source.droppableId);

					if (source) {
						/* Get the moved item */
						const item = source.items[dnd.source.index];

						/* Find destination section */
						const destination = result.find((section) => section.id === source.id);

						if (destination) {
							/* Remove item from current index and place in new index */
							destination.items.splice(dnd.source.index, 1);
							destination.items.splice(dnd.destination.index, 0, item);
						}
					}
				} else if (dnd.type === 'SECTION') {
					const [section] = result.splice(dnd.source.index, 1);
					result.splice(dnd.destination!.index, 0, section);
				}

				setValue(result);
			}}
		>
			<Droppable
				droppableId="droppable"
				type="SECTION"
				renderClone={(provided, snapshot, rubric) => (
					<div {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
						{renderSection(sections[rubric.source.index], rubric.source.index, provided, snapshot)}
					</div>
				)}
			>
				{(provided) => (
					<div ref={provided.innerRef} {...provided.droppableProps}>
						{sections.map((section, index) => (
							<Draggable key={section.id} draggableId={section.id} index={index}>
								{(provided, snapshot) => (
									<Box mb={3} ref={provided.innerRef} {...provided.draggableProps}>
										{renderSection(section, index, provided, snapshot)}
									</Box>
								)}
							</Draggable>
						))}
						{provided.placeholder}
					</div>
				)}
			</Droppable>
			<Separator />
			<Box mt={3}>
				<Button
					type="secondary"
					title={t('common.settings.evaluationForms.addSection')}
					icon={<AddIcon />}
					onClick={() =>
						setValue(
							sections.concat([
								{
									id: `__${Math.random()}`,
									items: [],
								},
							]),
						)
					}
				/>
			</Box>
		</DragDropContext>
	);
}
